import { motion } from 'framer-motion';
import { Instagram, Mail, ArrowUp } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const quickLinks = [
  { name: 'Home', href: '#home' },
  { name: 'About', href: '#about' },
  { name: 'Services', href: '#services' },
  { name: 'Pricing', href: '#pricing' },
  { name: 'Gallery', href: '#gallery' },
  { name: 'Contact', href: '#contact' },
];

export const Footer = () => {
  const location = useLocation();
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      window.location.href = '/' + href;
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#0a0a0a] pt-16 pb-8 relative" data-testid="footer">
      {/* Red divider */}
      <div className="section-divider mb-16" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16"
        >
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('#home'); }} className="inline-block mb-6">
              <span className="text-3xl font-bold tracking-tighter" style={{ fontFamily: 'Anton, sans-serif' }}>
                <span className="text-[#e62020]">VORTEX</span>
                <span className="text-white">WEBCO</span>
              </span>
            </a>
            <p className="text-[#888888] mb-6 max-w-sm leading-relaxed">
              No website? We build it. Old website? We rebuild it. From zero to live. From outdated to next gen.
            </p>
            <div className="flex gap-4">
              <a
                href="mailto:VortexWebCo@gmail.com"
                className="w-10 h-10 flex items-center justify-center border border-white/20 hover:border-[#e62020] hover:bg-[#e62020]/10 transition-colors"
                data-testid="footer-email"
                aria-label="Email us"
              >
                <Mail size={18} className="text-gray-400 hover:text-[#e62020]" />
              </a>
              <a
                href="https://www.instagram.com/vortexwebco/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center border border-white/20 hover:border-[#e62020] hover:bg-[#e62020]/10 transition-colors"
                data-testid="footer-instagram"
                aria-label="Follow us on Instagram"
              >
                <Instagram size={18} className="text-gray-400 hover:text-[#e62020]" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-6" style={{ fontFamily: 'Anton, sans-serif' }}>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                    className="text-gray-500 hover:text-[#e62020] transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-6" style={{ fontFamily: 'Anton, sans-serif' }}>
              Contact
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:VortexWebCo@gmail.com"
                  className="text-gray-500 hover:text-[#e62020] transition-colors text-sm"
                >
                  VortexWebCo@gmail.com
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/vortexwebco/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-[#e62020] transition-colors text-sm"
                >
                  @vortexwebco
                </a>
              </li>
            </ul>
          </div>
        </motion.div>

        {/* Faith Statement */}
        <div className="border-t border-white/10 pt-10 pb-8 text-center">
          <p className="text-white/80 text-base md:text-lg tracking-wide flex items-center justify-center gap-3">
            <span className="text-[#e62020] text-xl">✝</span>
            <span>Everything we do, we do for God.</span>
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
            <p className="text-[#888888] text-sm">
              © 2026 VortexWebCo. All rights reserved.
            </p>
            <span className="text-gray-700 hidden md:inline">|</span>
            <Link
              to="/privacy"
              className="text-gray-600 hover:text-[#e62020] transition-colors text-sm"
              data-testid="footer-privacy"
            >
              Privacy Policy
            </Link>
            <span className="text-gray-700">|</span>
            <Link
              to="/terms"
              className="text-gray-600 hover:text-[#e62020] transition-colors text-sm"
              data-testid="footer-terms"
            >
              Terms
            </Link>
            <span className="text-gray-700">|</span>
            <Link
              to="/service-agreement"
              className="text-gray-600 hover:text-[#e62020] transition-colors text-sm"
              data-testid="footer-service-agreement"
            >
              Service Agreement
            </Link>
          </div>
          <p className="text-gray-700 text-xs italic">
            Built by three guys who refused to quit.
          </p>
          <button
            onClick={scrollToTop}
            className="w-10 h-10 flex items-center justify-center border border-white/20 hover:border-[#e62020] hover:bg-[#e62020]/10 transition-colors"
            data-testid="scroll-to-top"
            aria-label="Scroll to top"
          >
            <ArrowUp size={18} className="text-gray-400" />
          </button>
        </div>
      </div>
    </footer>
  );
};
